import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxRootState } from "../../shared/redux/store";
import { ISelectItem, ReduxSliceState } from "../../shared/types";
import { parseOrderReports, withPayloadType } from "../../shared/utils";
import { OrderItem, OrderItemAttachment, OrderItemReports, OrganizationItem, UserGroupTest, UserGroupTestSpecimen, UsersResponse } from "../../api/types";
import { CreateOrderProps,OrderGridParams,  UpdateOrderProps, LoadOrderProps, LoadOrdersProps, ReduxOrdersSearchState, ViewOrderProps, LoadOrderAttachments, LoadOrderReports, DuplicateOrderProps, SendLabMessageProps, LoadOrderAvailableTestsPayload, LoadOrderOrganizationAvailableTestsPayload, LoadOrderOrganizationUsersPayload } from "../types";
import { FilterOrdersProps } from "../../organizations/search/types";

export const defaultOrderGridParams: OrderGridParams = {
    organization: undefined,
    organizationState: undefined,
    patientName: undefined,
    dateOfBirth: undefined,
    testCode: undefined,
    sampleId: undefined,
    providerName: undefined,
    providerType: undefined,
    needsContact: undefined,
    newReportStatus: undefined,
    orderStatus: undefined,
    orderStartDate: undefined,
    orderEndDate: undefined,
    reportDateStart: undefined,
    reportDateEnd: undefined,
    search: undefined,
};

const initialState: ReduxOrdersSearchState = {
    state: 'BUSY',
    selectedOrderMessagesState: 'READY',
    orderGridParams: {
        sortCol: 'orderId',
        sortDir: 'desc',
        limit: 500,
        previousPage: undefined,
        nextPage: undefined,
        // Defaults to the Lab Orders tab - explicitly false (not undefined) so the very first
        // load already excludes kit orders instead of returning both mixed together.
        isKitOrder: false,
        ...defaultOrderGridParams
    },
    orders: [],
    selectedOrder: undefined,
    selectedOrderRowIndex: undefined,
    selectedOrderReports: [],
    selectedOrderAttachments: undefined,
    selectedTab: 0,
    currentBase64File: undefined,
    uploadAttachmentBase64File: undefined,
    saveAttachmentState: 'READY',
    orderState: 'READY',
    orderOrganization: undefined,
    orderOrganizations: [],
    orderProviders: [],
    orderAvailableTests: [],
    filterPaneOpen: false,
    filterAvailableTests: [],
};

export const loadOrderOrganizationProviders = createAction('order/add-edit/loadOrganizationProviders', withPayloadType<LoadOrderOrganizationUsersPayload>());
export const loadOrderProviders = createAction('orders/add-edit/loadProviders');
export const loadOrderTests = createAction('orders/add-edit/loadOrderTests');
export const loadOrderOrganizationTests = createAction('orders/add-edit/loadOrderOrganizationTests', withPayloadType<LoadOrderOrganizationAvailableTestsPayload>());
export const loadOrderOrganization = createAction('orders/add-edit/loadOrderOrganization');
export const loadOrderOrganizations = createAction('orders/add-edit/loadOrderOrganizations');
export const loadOrders = createAction('orders/search/loadOrders', withPayloadType<LoadOrdersProps>());
export const loadOrdersNow = createAction('orders/search/loadOrdersNow', withPayloadType<LoadOrdersProps>());
export const filterOrders = createAction('orders/search/filterOrders', withPayloadType<LoadOrdersProps>());
// Pre-Buy Phase 6 - Kit Orders now search nuvia_orders via a dedicated endpoint (see
// NuviaOrdersController server-side), but still land in this same slice's `orders`/`orderGridParams`
// state (mapped to OrderItem's shape) so KitOrdersRow/the filter UI didn't need a parallel rewrite.
export const loadKitOrders = createAction('orders/search/loadKitOrders', withPayloadType<LoadOrdersProps>());
export const filterKitOrders = createAction('orders/search/filterKitOrders', withPayloadType<LoadOrdersProps>());
export const loadOrder = createAction('orders/search/loadOrder', withPayloadType<LoadOrderProps>());
export const viewOrder = createAction('orders/search/viewOrder', withPayloadType<ViewOrderProps>());
export const loadOrderAttachments = createAction('orders/search/loadOrderAttachments', withPayloadType<LoadOrderAttachments>());
export const loadOrderReports = createAction('orders/search/loadOrderReports', withPayloadType<LoadOrderReports>());
export const saveOrderAttachment = createAction('orders/search/saveOrderAttachment');
export const updateOrder = createAction('orders/add-edit/updateOrder', withPayloadType<UpdateOrderProps>());
export const createOrder = createAction('orders/add-edit/createOrder', withPayloadType<CreateOrderProps>());
export const duplicateOrder = createAction('orders/search/duplicateOrder', withPayloadType<DuplicateOrderProps>());
export const resetOrderForm = createAction('orders/add-edit/resetOrderForm', withPayloadType<void>());
export const sendLabMessage = createAction('orders/add-edit/messageLab', withPayloadType<SendLabMessageProps>());
export const markLabMessageRead = createAction('orders/add-edit/markLabMessageRead');
export const loadOrderEdit = createAction('orders/add-edit/loadOrderEdit', withPayloadType<number | string>());
export const loadOrderCreate = createAction('orders/add-edit/loadOrderCreate');
export const loadFilterAvailableTests = createAction('orders/search/loadFilterAvailableTests');

const ordersSearchSlice = createSlice({
    name: 'orders/search',
    initialState,
    reducers: {
        setState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.state = action.payload;
        },
        setSelectedOrderMessagesState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.selectedOrderMessagesState = action.payload;
        },
        setUploadAttachmentState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.saveAttachmentState = action.payload;
        },
        setOrderState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.orderState = action.payload;
        },
        setOrders: (state, action: PayloadAction<OrderItem[]>) => {
            state.orders = action.payload;
        },
        updateGridParams: (state, action: PayloadAction<Partial<OrderGridParams>>) => {
            state.orderGridParams = {
                ...state.orderGridParams,
                ...action.payload,
            };
        },
        clearGridFilters: (state) => {
            // isKitOrder drives the Lab Orders/Kit Orders tab, not a filter chip - "Clear filters"
            // shouldn't also switch tabs out from under the user.
            state.orderGridParams = { ...defaultOrderGridParams, isKitOrder: state.orderGridParams.isKitOrder };
        },
        setSelectedOrder: (state, action: PayloadAction<OrderItem | undefined>) => {
            state.selectedOrder = action.payload;
        },
        setSelectedOrderRowIndex: (state, action: PayloadAction<number | undefined>) => {
            state.selectedOrderRowIndex = action.payload;
        },
        setOrderDetailsTab: (state, action: PayloadAction<number | OrderItem | undefined>) => {
            const { payload } = action;
            if(payload === undefined){
                state.selectedTab = 0;
                return;
            }

            if(!isNaN(Number(payload))){
                state.selectedTab = Number(payload);
                return;
            }
        },
        setSelectedOrderReports: (state, action: PayloadAction<OrderItem | OrderItemReports | undefined>) => {
            state.selectedOrderReports = action.payload ? [...parseOrderReports(action.payload)] : [];
        },
        setSelectedOrderAttachments: (state, action: PayloadAction<OrderItemAttachment | undefined>) => {
            state.selectedOrderAttachments = action.payload;  
        },
        setViewFile: (state, action: PayloadAction<string | undefined>) => {
            state.currentBase64File = action.payload;
        },
        setAttachmentUploadFile: (state, action: PayloadAction<string | undefined>) => {
            state.uploadAttachmentBase64File = action.payload;
        },
        setOrderOrganization: (state, action: PayloadAction<OrganizationItem | undefined>) => {
            state.orderOrganization = {...action.payload};
        },
        setOrderOrganizations: (state, action: PayloadAction<OrganizationItem[]>) => {
            state.orderOrganizations = [...action.payload];
        },
        setOrderProviders: (state, action: PayloadAction<UsersResponse>) => {
            state.orderProviders = action.payload;
        },
        setOrderTests: (state, action: PayloadAction<UserGroupTest[]>) => {
            state.orderAvailableTests = action.payload;
        },
        toggleFilterPane: (state) => {
            state.filterPaneOpen = !state.filterPaneOpen;
        },
        setFilterAvailableTests: (state, action: PayloadAction<UserGroupTest[]>) => {
            state.filterAvailableTests = action.payload;
        },
    }
});

export default ordersSearchSlice;

const selectSelf = (state: ReduxRootState) => state.ordersSearch;
export const selectOrdersSearchIsBusy = createSelector(selectSelf, self => self.state === 'BUSY');
export const selectOrdersSearchSelectedOrderMessagesState = createSelector(selectSelf, self => self.selectedOrderMessagesState);
export const selectOrdersSearchSelectedOrderMessagesStateIsBusy = createSelector(selectOrdersSearchSelectedOrderMessagesState, state => state === 'BUSY');
export const selectOrdersSearchOrders = createSelector(selectSelf, self => self.orders);
export const selectOrdersSearchGridParams = createSelector(selectSelf, self => self.orderGridParams);
export const selectOrdersSearchGridPaging = createSelector(selectOrdersSearchGridParams, ({ previousPage, nextPage }) => ({
    previousPage,
    nextPage
}));
export const selectOrdersSearchGridSearch = createSelector(selectOrdersSearchGridParams, ({ search }) => search);
export const selectOrdersSearchGridIsKitOrder = createSelector(selectOrdersSearchGridParams, ({ isKitOrder }) => Boolean(isKitOrder));
export const selectOrdersSearchGridFilterOrg = createSelector(selectOrdersSearchGridParams, ({ organization }) => organization);
export const selectOrdersSearchGridFilterOrgState = createSelector(selectOrdersSearchGridParams, ({ organizationState }) => organizationState);
export const selectOrdersSearchGridFilterPatientName = createSelector(selectOrdersSearchGridParams, ({ patientName }) => patientName);
export const selectOrdersSearchGridFilterPatientDOB = createSelector(selectOrdersSearchGridParams, ({ dateOfBirth }) => dateOfBirth);
export const selectOrdersSearchGridFilterSampleID = createSelector(selectOrdersSearchGridParams, ({ sampleId }) => sampleId);
export const selectOrdersSearchGridFilterTestCode = createSelector(selectOrdersSearchGridParams, ({ testCode }) => testCode);
export const selectOrdersSearchGridFilterProviderName = createSelector(selectOrdersSearchGridParams, ({ providerName }) => providerName);
export const selectOrdersSearchGridFilterProviderType = createSelector(selectOrdersSearchGridParams, ({ providerType }) => providerType);
export const selectOrdersSearchGridFilterNeedsContact = createSelector(selectOrdersSearchGridParams, ({ needsContact }) => needsContact);
export const selectOrdersSearchGridFilterNewReport = createSelector(selectOrdersSearchGridParams, ({ newReportStatus }) => newReportStatus);
export const selectOrdersSearchGridFilterOrderStatus = createSelector(selectOrdersSearchGridParams, ({ orderStatus }) => orderStatus);
export const selectOrdersSearchGridFilterOrderStateDate = createSelector(selectOrdersSearchGridParams, ({ orderStartDate }) => orderStartDate);
export const selectOrdersSearchGridFilterOrderEndDate = createSelector(selectOrdersSearchGridParams, ({ orderEndDate }) => orderEndDate);
export const selectOrdersSearchGridFilterOrderReportDateStart = createSelector(selectOrdersSearchGridParams, ({ reportDateStart }) => reportDateStart);
export const selectOrdersSearchGridFilterOrderReportDateEnd = createSelector(selectOrdersSearchGridParams, ({ reportDateEnd }) => reportDateEnd);

export const selectOrdersSearchGridFiltersApplied = createSelector(selectOrdersSearchGridParams, params => {
    let filtersApplied = 0;
    Object.keys(defaultOrderGridParams).forEach(value => {
        if(params[value] !== defaultOrderGridParams[value])
            filtersApplied++;
    });
    return filtersApplied;
});
export const selectOrdersSearchSelectedOrder = createSelector(selectSelf, self => self.selectedOrder);
export const selectOrdersSearchDetailPaneOpen = createSelector(selectOrdersSearchSelectedOrder, order => order !== undefined);
export const selectOrdersSearchSelectedOrderRowIndex = createSelector(selectSelf, self => self.selectedOrderRowIndex);
export const selectOrdersSearchSelectedOrderLabMessage = createSelector(selectOrdersSearchSelectedOrder, order => order?.internalComment);
export const selectOrdersSearchSelectedOrderLabMessageNeedsContact = createSelector(selectOrdersSearchSelectedOrder, order => order?.needsContact);
export const selectOrdersSearchSelectedOrderReports = createSelector(selectSelf, self => self.selectedOrderReports);
export const selectOrdersSearchSelectedOrderAttachments = createSelector(selectSelf, self => self.selectedOrderAttachments);
export const selectOrdersSearchSelectedOrderViewFile = createSelector(selectSelf, self => self.currentBase64File);
export const selectOrdersSearchSelectedOrderId = createSelector(selectOrdersSearchSelectedOrder, order => order?.orderId);
export const selectOrdersSearchAvailableTests = createSelector(selectSelf, order => order.orderAvailableTests);
export const selectOrdersSearchAvailableTestsList = createSelector(selectOrdersSearchAvailableTests, tests => tests.map<ISelectItem>(test => ({
    label: test.name,
    value: test.code,
})));
export const selectOrdersSearchAvailableTestsNames = createSelector(selectOrdersSearchAvailableTests, tests => tests.map(test => test.name));
export const selectOrdersSearchTestSpecimens = createSelector(selectSelf, order => order.orderAvailableTests.flatMap((x: UserGroupTest) => x.specimenSources));
export const selectOrdersSearchTestQuestions = createSelector(selectSelf, order => order.orderAvailableTests.flatMap((x: UserGroupTest) => x.questions));

export const selectOrdersSearchOrderDetailsTab = createSelector(selectSelf, self => self.selectedTab);
export const selectOrdersSearchUploadAttachmentFile = createSelector(selectSelf, self => self.uploadAttachmentBase64File);
export const selectOrdersSearchUploadAttachmentState = createSelector(selectSelf, self => self.saveAttachmentState);
export const selectOrdersSearchUploadAttachmentIsBusy = createSelector(selectOrdersSearchUploadAttachmentState, state => state === 'BUSY');
export const selectOrdersSearchOrderState = createSelector(selectSelf, self => self.orderState);
export const selectOrdersSearchOrderIsBusy = createSelector(selectOrdersSearchOrderState, orderState => orderState === 'BUSY');
export const selectOrdersSearchOrderIsSuccess = createSelector(selectOrdersSearchOrderState, orderState => orderState === 'SUCCESS');
export const selectOrdersSearchOrderFormNeedsReset = createSelector(selectOrdersSearchOrderState, orderState => orderState === 'RESET');
export const selectOrdersSearchOrderOrganization = createSelector(selectSelf, self => self.orderOrganization);
export const selectOrdersSearchOrderOrganizations = createSelector(selectSelf, self => self.orderOrganizations);
export const selectOrdersSearchOrderOrganizationItems = createSelector(selectOrdersSearchOrderOrganizations, orgs => orgs.map<ISelectItem>(org => ({
    value: org.orgId?.toString() ?? "",
    label: org.name!,
})));
export const selectOrdersSearchFilterOrganizationItems = createSelector(selectOrdersSearchOrderOrganizations, orgs => orgs.map<ISelectItem>(org => ({
    value: org.name!,
    label: org.name!,
})));
export const selectOrdersSearchProviders = createSelector(selectSelf, self => self.orderProviders);
export const selectOrdersSearchFilterPaneShow = createSelector(selectSelf, self => self.filterPaneOpen);

export const selectOrdersSearchFilterAvailableTests = createSelector(selectSelf, order => order.filterAvailableTests);
export const selectOrdersSearchFilterAvailableTestsList = createSelector(selectOrdersSearchFilterAvailableTests, tests => tests.map<ISelectItem>(test => ({
    label: test.name,
    value: test.code,
})));
